import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeepKeys } from "@tanstack/react-table";
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_RowData,
  MRT_Updater,
} from "material-react-table";
import { useDebounce } from "Hooks/useDebounce";
import { SortDirection } from "Infrastructure/Api/Api";

// Column ids MRT reports in its sorting/filtering state: accessorKeys (typed
// by MRT as DeepKeys<TData>) plus any custom `id`s of computed columns, which
// tables with such columns must list explicitly via TExtraColumnId.
type ServerTableColumnId<
  TData extends MRT_RowData,
  TExtraColumnId extends string,
> = DeepKeys<TData> | TExtraColumnId;

export type ServerTableQueryParams<
  TData extends MRT_RowData,
  TExtraColumnId extends string = never,
> = {
  offset: number;
  limit: number;
  sortBy?: ServerTableColumnId<TData, TExtraColumnId>;
  sortDirection: SortDirection;
  filters: Partial<Record<ServerTableColumnId<TData, TExtraColumnId>, unknown>>;
  globalFilter?: string;
};

type UseServerTableOptions = {
  defaultPageSize?: number;
  defaultSorting: MRT_SortingState;
  filterDebounceMs?: number;
  isGlobalFilterEnabled?: boolean;
  areColumnFiltersEnabled?: boolean;
  /** Filters the table starts with, e.g. seeded from a deep link. Read on mount only. */
  initialColumnFilters?: MRT_ColumnFiltersState;
};

type UseServerTableResult<
  TData extends MRT_RowData,
  TExtraColumnId extends string = never,
> = {
  queryParams: ServerTableQueryParams<TData, TExtraColumnId>;
  tableProps: Partial<MRT_TableOptions<TData>>;
  resetPagination: () => void;
};

// muiFilterDatePickerProps below forces a ~160px filter input; date-filter
// columns narrower than this clip the calendar icon behind the next column.
export const DATE_FILTER_COLUMN_MIN_SIZE = 175;

export const useServerTable = <
  TData extends MRT_RowData,
  TExtraColumnId extends string = never,
>(
  options: UseServerTableOptions,
): UseServerTableResult<TData, TExtraColumnId> => {
  const {
    defaultPageSize = 15,
    defaultSorting,
    filterDebounceMs = 400,
    isGlobalFilterEnabled = false,
    areColumnFiltersEnabled = true,
    initialColumnFilters = [],
  } = options;

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>(defaultSorting);
  const [columnFilters, setColumnFilters] =
    useState<MRT_ColumnFiltersState>(initialColumnFilters);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [isFilterRowVisible, setIsFilterRowVisible] = useState(
    areColumnFiltersEnabled,
  );
  const [isGlobalFilterVisible, setIsGlobalFilterVisible] = useState(
    isGlobalFilterEnabled,
  );

  const debouncedFilters = useDebounce(columnFilters, filterDebounceMs);
  const debouncedGlobalFilter = useDebounce(globalFilter, filterDebounceMs);

  const resetPagination = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Reset pagination when debounced filters/search change (not on keystroke)
  // to avoid a spurious query with stale filters before debounce settles
  const isFirstFilterRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedFilters is the intentional trigger
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    resetPagination();
  }, [debouncedFilters]);

  const isFirstGlobalFilterRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedGlobalFilter is the intentional trigger
  useEffect(() => {
    if (isFirstGlobalFilterRender.current) {
      isFirstGlobalFilterRender.current = false;
      return;
    }
    resetPagination();
  }, [debouncedGlobalFilter]);

  const queryParams = useMemo((): ServerTableQueryParams<
    TData,
    TExtraColumnId
  > => {
    // MRT state carries column ids as plain strings, but they originate from
    // column accessorKeys (typed by MRT as DeepKeys<TData>) or custom ids.
    const filters = Object.fromEntries(
      debouncedFilters.map(({ id, value }) => [id, value]),
    ) as ServerTableQueryParams<TData, TExtraColumnId>["filters"];

    return {
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      sortBy: sorting[0]?.id as
        | ServerTableColumnId<TData, TExtraColumnId>
        | undefined,
      sortDirection: sorting[0]?.desc
        ? SortDirection.Descending
        : SortDirection.Ascending,
      filters,
      globalFilter: debouncedGlobalFilter || undefined,
    };
  }, [pagination, sorting, debouncedFilters, debouncedGlobalFilter]);

  const handleColumnFiltersChange = useCallback(
    (updater: MRT_Updater<MRT_ColumnFiltersState>) => {
      setColumnFilters(updater);
    },
    [],
  );

  const handleGlobalFilterChange = useCallback(
    (updater: MRT_Updater<string>) => {
      setGlobalFilter((prev) =>
        typeof updater === "function" ? updater(prev) : (updater ?? ""),
      );
    },
    [],
  );

  const tableProps = useMemo(
    (): Partial<MRT_TableOptions<TData>> => ({
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      enableGlobalFilter: isGlobalFilterEnabled,
      enableColumnFilters: areColumnFiltersEnabled,
      enableMultiSort: false,
      onPaginationChange: setPagination,
      onSortingChange: (updater: MRT_Updater<MRT_SortingState>) => {
        setSorting(updater);
        resetPagination();
      },
      onColumnFiltersChange: handleColumnFiltersChange,
      onGlobalFilterChange: handleGlobalFilterChange,
      onShowColumnFiltersChange: setIsFilterRowVisible,
      onShowGlobalFilterChange: setIsGlobalFilterVisible,
      muiFilterDatePickerProps: {
        slotProps: {
          textField: {
            size: "small",
            sx: {
              minWidth: "160px",
              mx: "-2px",
              p: 0,
              width: "calc(100% + 4px)",
              "& .MuiInputAdornment-root": { ml: 0 },
              "& .MuiIconButton-root": { p: "2px" },
              "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
              "& .MuiPickersInputBase-sectionsContainer": {
                py: 0,
                fontSize: "0.875rem",
              },
              "& .MuiPickersInputBase-root": {
                minHeight: 0,
                height: "1.75em",
              },
            },
          },
        },
      },
      // Filter-row visibility is controlled state (not initialState) on
      // purpose: initialState is read only on the first mount of the MRT
      // instance, so a consumer that first renders the table without
      // tableProps (e.g. a loading-skeleton branch) would lock the filter
      // row hidden for good.
      state: {
        pagination,
        sorting,
        columnFilters,
        globalFilter,
        showColumnFilters: isFilterRowVisible,
        showGlobalFilter: isGlobalFilterVisible,
      },
    }),
    [
      pagination,
      sorting,
      columnFilters,
      globalFilter,
      isFilterRowVisible,
      isGlobalFilterVisible,
      isGlobalFilterEnabled,
      areColumnFiltersEnabled,
      handleColumnFiltersChange,
      handleGlobalFilterChange,
      resetPagination,
    ],
  );

  return { queryParams, tableProps, resetPagination };
};
